import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { queryKeys } from 'core/api/queryKeys';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { assessmentApi } from 'features/assessment-dashboard/assessment.api';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { Button } from 'shared/components/Button';
import { ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { useToast } from 'shared/components/Toast';
import { SignedRadialChart } from 'shared/charts/SignedRadialChart';
import { WeightInput } from 'shared/forms/Fields';
import { formatDateTime, formatSignedWeight, parseWeight } from 'shared/utilities/format';
import { useUnsavedChanges } from 'shared/utilities/useUnsavedChanges';

export function AnswerDetailPage() {
  const { id } = useParams();
  const { t, locale } = useI18n();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canEdit = hasPermission(PERMISSIONS.weightsManage);
  const [drafts, setDrafts] = useState(null);
  const query = useQuery({
    queryKey: queryKeys.answer(id),
    queryFn: ({ signal }) => assessmentApi.showAnswer(id, { signal }),
  });
  const answer = query.data?.answer || query.data;
  const weights = useMemo(() => answer?.weights || [], [answer]);
  const values = drafts || weights.map((item) => ({
    professional_pattern_id: item.professional_pattern_id,
    weight: item.weight,
  }));
  const dirty = Boolean(drafts);
  useUnsavedChanges(dirty);

  const save = useMutation({
    mutationFn: () => assessmentApi.saveAnswerWeights(id, values.map((item) => ({
      professional_pattern_id: item.professional_pattern_id,
      weight: parseWeight(item.weight),
    }))),
    onSuccess: async () => {
      setDrafts(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.answer(id) });
      toast.success(t('status.saved'));
    },
    onError: (error) => toast.error(humanizeApiError(error, t)),
  });

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;

  const chartItems = weights.map((item) => ({
    ...item,
    ...(item.pattern || {}),
    professional_pattern_id: item.professional_pattern_id,
    weight: values.find((row) => row.professional_pattern_id === item.professional_pattern_id)?.weight ?? item.weight,
  }));

  return (
    <div>
      <PageHeader
        title={t('answers.detail')}
        actions={canEdit ? (
          <Button disabled={save.isPending || !dirty} loading={save.isPending} onClick={() => save.mutate()}>
            {t('actions.save')}
          </Button>
        ) : null}
      />
      <p>{locale === 'ar' ? answer?.text_ar : answer?.text_en}</p>
      <p className="muted">{t('answers.lastUpdated')}: {formatDateTime(answer?.updated_at, locale)}</p>
      <p>{t('answers.chartSummary')}</p>
      <SignedRadialChart weights={chartItems} locale={locale} />
      {canEdit ? (
        <div className="weight-list">
          <h2>{t('weights.answerEditor')}</h2>
          {values.map((item, index) => {
            const meta = weights.find((weight) => weight.professional_pattern_id === item.professional_pattern_id);
            return (
              <div key={item.professional_pattern_id} className="matrix-row">
                <span className="color-dot" style={{ background: meta?.pattern?.color }} />
                <span>{locale === 'ar' ? meta?.pattern?.name_ar : meta?.pattern?.name_en}</span>
                <WeightInput
                  id={`answer-weight-${item.professional_pattern_id}`}
                  value={item.weight}
                  onChange={(value) => {
                    const next = values.map((row, rowIndex) => (rowIndex === index ? { ...row, weight: value } : row));
                    setDrafts(next);
                  }}
                />
                <span>{formatSignedWeight(item.weight)}</span>
              </div>
            );
          })}
        </div>
      ) : null}
      <Link to={paths.builder}>{t('actions.back')}</Link>
    </div>
  );
}
